import {HttpErrorUtils, HttpResponseErrorBoltServer, HttpClientError} from "@bolteu/http-client-bolt-server";
import {ErrorType} from "config/errors";

interface ErrorNotification {
    message: string;
    errorType: string;
    metadata?: Record<string, unknown>;
}

// The rejected async-thunk actions rejectionLogger feeds in (RTK v2 deprecated AnyAction;
// payload/error stay unknown and are narrowed below).
interface RejectedActionLike {
    type: string;
    payload?: unknown;
    error?: unknown;
}

const hasName = (value: unknown): value is {name: string} =>
    typeof value === "object" && value !== null && "name" in value;

export const getErrorNotification = (action: RejectedActionLike): ErrorNotification => {
    const {payload: backendError, error: otherError} = action;

    const payload = backendError ? backendError : otherError;

    if (typeof payload === "string") {
        return {
            message: action.type,
            errorType: payload,
            metadata: {action},
        };
    }

    if (HttpErrorUtils.isInstanceOf(payload, HttpResponseErrorBoltServer.ValidationError)) {
        return {
            message: action.type,
            errorType: payload.name,
            metadata: {validatorResults: payload.validatorResults},
        };
    }

    if (HttpErrorUtils.isInstanceOf(payload, HttpResponseErrorBoltServer.ServerResponseError)) {
        return {
            message: action.type,
            errorType: payload.response.message,
            metadata: {
                code: payload.response.code,
                url: payload.url,
            },
        };
    }

    if (HttpErrorUtils.isInstanceOf(payload, HttpResponseErrorBoltServer.EntityNotFoundError)) {
        return {
            message: action.type,
            errorType: ErrorType.ENTITY_NOT_FOUND,
            metadata: {
                errorName: payload.name,
                entity: payload.entity,
                errorData: payload.errorData,
            },
        };
    }

    if (HttpErrorUtils.isInstanceOf(payload, HttpResponseErrorBoltServer.AuthTokenExpiredError)) {
        return {
            message: action.type,
            errorType: ErrorType.TOKEN_EXPIRED,
            metadata: {
                errorName: payload.name,
                expiredAt: payload.expiredAt,
            },
        };
    }

    if (
        HttpErrorUtils.isInstanceOf(payload, HttpClientError.RequestNetworkError) ||
        (hasName(payload) && payload.name === "HttpRequestNetworkError")
    ) {
        return {
            message: action.type,
            errorType: ErrorType.HTTP_REQUEST_NETWORK_ERROR,
        };
    }

    if (
        HttpErrorUtils.isInstanceOf(payload, HttpClientError.RequestAbortError) ||
        (hasName(payload) && payload.name === "HttpRequestAbortError")
    ) {
        return {
            message: action.type,
            errorType: ErrorType.HTTP_REQUEST_ABORT_ERROR,
        };
    }

    return {
        message: action.type,
        errorType: ErrorType.API_UNKNOWN_ERROR,
        metadata: {action},
    };
};
